export class C {
  constructor(
    public readonly x: number,
    public readonly y: number
  ) {}

  public equals(other: C): boolean {
    return this.x === other.x && this.y === other.y;
  }

  public toString(): string {
    return `${this.x},${this.y}`;
  }

  public toHash(): string {
    return `${this.x},${this.y}`;
  }

  /**
   * Parses a string in format "(x,y)" to a C object
   * @param str String in format "(x,y)"
   * @returns Parsed C object
   * @throws Error if the format is invalid
   */
  public static parse(str: string): C {
    const result = C.tryParse(str);
    if (!result.success) {
      throw new Error(`Invalid C format: ${str}. Expected format: "(x,y)"`);
    }
    return result.value!;
  }

  /**
   * Attempts to parse a string in format "(x,y)" to a C object
   * @param str String in format "(x,y)"
   * @returns Object with success flag and value (if successful)
   */
  public static tryParse(str: string): { success: boolean; value: C | null } {
    // Remove whitespace
    str = str.trim();

    // Check basic format: starts with (, ends with ), contains a comma
    if (!str.startsWith('(') || !str.endsWith(')') || !str.includes(',')) {
      return { success: false, value: null };
    }

    // Extract content between parentheses
    const content = str.substring(1, str.length - 1);

    // Split by comma
    const parts = content.split(',');
    if (parts.length !== 2) {
      return { success: false, value: null };
    }

    // Parse numbers
    const x = Number(parts[0]);
    const y = Number(parts[1]);

    // Check if both are valid numbers
    if (isNaN(x) || isNaN(y)) {
      return { success: false, value: null };
    }

    return { success: true, value: new C(x, y) };
  }

  /**
   * Alternative implementation that returns a tuple
   */
  public static tryParseTuple(str: string): [boolean, C | null] {
    const result = C.tryParse(str);
    return [result.success, result.value];
  }

  /**
   * Creates a C object from an array [x, y]
   */
  public static fromArray(arr: [number, number]): C {
    return new C(arr[0], arr[1]);
  }

  /**
   * Creates a C object from an object with x and y properties
   */
  public static fromObject(obj: { x: number; y: number }): C {
    return new C(obj.x, obj.y);
  }

  /**
   * Converts to array [x, y]
   */
  public toArray(): [number, number] {
    return [this.x, this.y];
  }
}
